import React, { useState, useRef } from 'react';
import { X, Download, Upload, FileJson, FileText, Check, AlertCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExportImportModal = ({ isOpen, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('export');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Export options
  const [exportFormat, setExportFormat] = useState('json');
  const [includeSRS, setIncludeSRS] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  
  // Import options
  const [importStrategy, setImportStrategy] = useState('skip');
  const [importData, setImportData] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const fileInputRef = useRef(null);

  // Fetch categories on open
  React.useEffect(() => {
    if (isOpen) {
      fetchCategories();
      setResult(null);
      setError(null);
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const betaCode = localStorage.getItem('betaCode');
      const headers = betaCode ? { 'X-Beta-Code': betaCode } : {};
      const response = await fetch(`${API_URL}/api/vocabulary/categories`, { headers });
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Export vocabulary
  const handleExport = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const params = new URLSearchParams({
        format: exportFormat,
        include_srs: includeSRS.toString()
      });
      if (selectedCategory) {
        params.append('category', selectedCategory);
      }
      
      const betaCode = localStorage.getItem('betaCode');
      const headers = betaCode ? { 'X-Beta-Code': betaCode } : {};
      const response = await fetch(`${API_URL}/api/vocabulary/export?${params}`, { headers });
      
      if (exportFormat === 'csv') {
        // Download CSV file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `thai_vocabulary_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        setResult({ type: 'export', message: 'Fichier CSV téléchargé !' });
      } else {
        // Download JSON file
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `thai_vocabulary_${new Date().toISOString().slice(0,10)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        setResult({ 
          type: 'export', 
          message: `${data.total_words} mots exportés !`,
          categories: data.categories?.length || 0
        });
      }
    } catch (err) {
      setError(`Erreur d'export: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection for import
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImportFileName(file.name);
    setError(null);
    setResult(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        
        if (file.name.endsWith('.json')) {
          const json = JSON.parse(content);
          // Handle both direct array and wrapped format
          const words = json.data || json.words || json;
          if (Array.isArray(words)) {
            setImportData(words);
          } else {
            throw new Error('Format JSON invalide');
          }
        } else if (file.name.endsWith('.csv')) {
          // Parse CSV
          const lines = content.split('\n').filter(l => l.trim());
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
          
          const words = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.trim());
            const word = {};
            headers.forEach((h, i) => {
              if (h === 'thai') word.thai = values[i];
              else if (h === 'french') word.french = values[i];
              else if (h === 'category') word.category = values[i];
            });
            return word;
          }).filter(w => w.thai && w.french);
          
          setImportData(words);
        } else {
          throw new Error('Format non supporté. Utilisez JSON ou CSV.');
        }
      } catch (err) {
        setError(`Erreur de lecture: ${err.message}`);
        setImportData(null);
      }
    };
    reader.readAsText(file);
  };

  // Import vocabulary
  const handleImport = async () => {
    if (!importData || importData.length === 0) {
      setError('Aucune donnée à importer');
      return;
    }
    
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const betaCode = localStorage.getItem('betaCode');
      const headers = { 'Content-Type': 'application/json' };
      if (betaCode) {
        headers['X-Beta-Code'] = betaCode;
      }
      
      const response = await fetch(`${API_URL}/api/vocabulary/import`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          words: importData,
          merge_strategy: importStrategy
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur d\'import');
      }
      
      setResult({
        type: 'import',
        imported: data.imported,
        skipped: data.skipped,
        updated: data.updated,
        errors: data.errors,
        message: `Import terminé: ${data.imported} ajoutés, ${data.skipped} ignorés, ${data.updated} mis à jour`
      });
      
      // Clear import data
      setImportData(null);
      setImportFileName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      // Notify parent
      if (onSuccess) onSuccess();
      
    } catch (err) {
      setError(`Erreur d'import: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl w-full max-w-md shadow-2xl border border-gray-700" data-testid="export-import-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-4 rounded-t-2xl flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              {activeTab === 'export' ? <Download className="w-5 h-5 text-white" /> : <Upload className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Vocabulaire</h2>
              <p className="text-teal-200 text-sm">Export & Import</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="close-export-import">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => { setActiveTab('export'); setResult(null); setError(null); }}
            className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 font-medium transition-colors ${
              activeTab === 'export' 
                ? 'text-teal-400 border-b-2 border-teal-400 bg-teal-500/10' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            data-testid="tab-export"
          >
            <Download className="w-4 h-4" />
            Exporter
          </button>
          <button
            onClick={() => { setActiveTab('import'); setResult(null); setError(null); }}
            className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 font-medium transition-colors ${
              activeTab === 'import' 
                ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-500/10' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            data-testid="tab-import"
          >
            <Upload className="w-4 h-4" />
            Importer
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {activeTab === 'export' ? (
            <>
              {/* Format selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Format</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setExportFormat('json')}
                    className={`p-3 rounded-lg border flex items-center gap-2 transition-colors ${
                      exportFormat === 'json' 
                        ? 'border-teal-500 bg-teal-500/20 text-teal-400' 
                        : 'border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <FileJson className="w-5 h-5" />
                    <span>JSON</span>
                  </button>
                  <button
                    onClick={() => setExportFormat('csv')}
                    className={`p-3 rounded-lg border flex items-center gap-2 transition-colors ${
                      exportFormat === 'csv' 
                        ? 'border-teal-500 bg-teal-500/20 text-teal-400' 
                        : 'border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    <FileText className="w-5 h-5" />
                    <span>CSV</span>
                  </button>
                </div>
              </div>

              {/* Category filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Catégorie (optionnel)</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-teal-500 focus:outline-none"
                >
                  <option value="">Toutes les catégories</option>
                  {categories.map(cat => (
                    <option key={cat.name} value={cat.name}>
                      {cat.name} ({cat.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Include SRS */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeSRS}
                  onChange={(e) => setIncludeSRS(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-500 bg-gray-700 text-teal-500 focus:ring-teal-500"
                />
                <span className="text-gray-300">Inclure la progression SRS</span>
              </label>

              {/* Export button */}
              <button
                onClick={handleExport}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
                data-testid="btn-export"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                {loading ? 'Export en cours...' : 'Exporter le vocabulaire'}
              </button>
            </>
          ) : (
            <>
              {/* File input */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Fichier à importer</label>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-cyan-500 transition-colors"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,.csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                  {importFileName ? (
                    <p className="text-cyan-400 font-medium">{importFileName}</p>
                  ) : (
                    <p className="text-gray-400">Cliquez pour sélectionner un fichier JSON ou CSV</p>
                  )}
                  {importData && (
                    <p className="text-sm text-gray-500 mt-1">{importData.length} mots détectés</p>
                  )}
                </div>
              </div>

              {/* Merge strategy */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">En cas de doublon</label>
                <div className="space-y-2">
                  {[
                    { value: 'skip', label: 'Ignorer', desc: 'Garder les mots existants' },
                    { value: 'overwrite', label: 'Remplacer', desc: 'Mettre à jour avec les nouveaux' },
                    { value: 'merge', label: 'Ajouter', desc: 'Garder les deux versions' }
                  ].map(option => (
                    <label 
                      key={option.value}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        importStrategy === option.value 
                          ? 'border-cyan-500 bg-cyan-500/20' 
                          : 'border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <input
                        type="radio"
                        name="strategy"
                        value={option.value}
                        checked={importStrategy === option.value}
                        onChange={(e) => setImportStrategy(e.target.value)}
                        className="text-cyan-500 focus:ring-cyan-500"
                      />
                      <div>
                        <span className="text-white font-medium">{option.label}</span>
                        <p className="text-xs text-gray-500">{option.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Import button */}
              <button
                onClick={handleImport}
                disabled={loading || !importData}
                className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
                data-testid="btn-import"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                {loading ? 'Import en cours...' : `Importer ${importData?.length || 0} mots`}
              </button>
            </>
          )}

          {/* Result message */}
          {result && (
            <div className="p-3 bg-green-500/20 border border-green-500/50 rounded-lg flex items-start gap-2">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-400 font-medium">{result.message}</p>
                {result.type === 'import' && (
                  <p className="text-xs text-green-300 mt-1">
                    {result.errors > 0 && `${result.errors} erreur(s)`}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportImportModal;
